import type { MainModule, MjData, MjModel, MjVFS } from "@mujoco/mujoco";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

import { STEPS_PER_FRAME, TIMESTEP } from "../protocol";
import { loadAssetPackage, meshUsesFaceNormals } from "./assets";
import { getMujoco } from "./mujoco";
import { disposeAppearance, loadAppearance, NEUTRAL_METALNESS, type Appearance } from "./appearance";
import { GROUND_CLEAR_COLOR, buildGround, type GroundFootprint, type GroundVisual } from "./ground";

export interface ViewerTelemetry {
  activeMs: number;
  dragCount: number;
  resetCount: number;
  jointCoverage: number;
  fps: number;
  simulationFaultCount?: number;
  faulted?: boolean;
  simulationRtf?: number;
  paused?: boolean;
}

export interface ViewerSnapshot {
  paused: boolean;
  physics: number[];
  camera: number[];
  target: number[];
  telemetry: ViewerTelemetry;
  coverage: Array<[number, number]>;
}

export interface ViewerCoreOptions {
  canvas: HTMLCanvasElement;
  assetUrl: string;
  onTelemetry?: (telemetry: ViewerTelemetry) => void;
  appearanceUrl?: string | null;
  /** Unit viewing direction in the three.js frame; changes presentation only. */
  initialCameraDirection?: readonly [number, number, number];
}

export interface MovablePart {
  id: number;
  number: number;
  motions: Array<"hinge" | "slide" | "ball">;
}

interface RenderGeom {
  geomId: number;
  bodyId: number;
  object: THREE.Mesh;
  localMatrix?: THREE.Matrix4;
}

interface DragState {
  bodyId: number;
  geomId: number;
  localAnchor: THREE.Vector3;
  previousAnchor: THREE.Vector3;
  target: THREE.Vector3;
  plane: THREE.Plane;
  pointerId: number;
}

export interface ViewerDragDebug {
  bodyId: number;
  geomId: number;
  mass: number;
  anchor: [number, number, number];
  target: [number, number, number];
  error: number;
  cameraRight: [number, number, number];
}

interface CoverageJoint {
  qposAddress: number;
  lower: number;
  upper: number;
  visitedMin: number;
  visitedMax: number;
}

interface ViewerDebugStats {
  created: number;
  disposed: number;
  live: number;
}

declare global {
  interface Window {
    __US_VIEWER_STATS__?: ViewerDebugStats;
  }
}

const Y_AXIS = new THREE.Vector3(0, 1, 0);
const DEFAULT_GEOM_RGBA = [0.5, 0.5, 0.5, 1] as const;
const DRAG_NATURAL_FREQUENCY = 28;
const DRAG_MAX_ACCELERATION = 250;

function updateDebugStats(deltaCreated: number, deltaDisposed: number): void {
  const stats = window.__US_VIEWER_STATS__ ?? { created: 0, disposed: 0, live: 0 };
  stats.created += deltaCreated;
  stats.disposed += deltaDisposed;
  stats.live = stats.created - stats.disposed;
  window.__US_VIEWER_STATS__ = stats;
}

export function c1ShouldRender(module: MainModule, model: MjModel, geomId: number): boolean {
  const visual = model.geom_contype[geomId] === 0 && model.geom_conaffinity[geomId] === 0;
  const name = module.mj_id2name(model, module.mjtObj.mjOBJ_GEOM.value, geomId) || "";
  return visual || name.startsWith("us_scene_");
}

function meshGeometry(model: MjModel, meshId: number): THREE.BufferGeometry {
  const vertexAddress = model.mesh_vertadr[meshId] as number;
  const vertexCount = model.mesh_vertnum[meshId] as number;
  const normalAddress = model.mesh_normaladr[meshId] as number;
  const normalCount = model.mesh_normalnum[meshId] as number;
  const faceAddress = model.mesh_faceadr[meshId] as number;
  const faceCount = model.mesh_facenum[meshId] as number;
  const positions = new Float32Array(vertexCount * 3);
  const normals = new Float32Array(normalCount * 3);
  const indices = new Uint32Array(faceCount * 3);
  positions.set(model.mesh_vert.subarray(vertexAddress * 3, (vertexAddress + vertexCount) * 3));
  normals.set(model.mesh_normal.subarray(normalAddress * 3, (normalAddress + normalCount) * 3));
  indices.set(model.mesh_face.subarray(faceAddress * 3, (faceAddress + faceCount) * 3));
  const normalIndices = model.mesh_facenormal.subarray(faceAddress * 3, (faceAddress + faceCount) * 3) as Int32Array;
  if (indices.some((vertex, corner) => vertex !== normalIndices[corner])) {
    // MuJoCo indexes positions and authored normals separately; Three.js shares
    // one index. Expand corners only when the two index streams differ.
    const corners = new Float32Array(faceCount * 9);
    const cornerNormals = new Float32Array(faceCount * 9);
    for (let corner = 0; corner < indices.length; corner += 1) {
      const vertex = indices[corner]! * 3;
      const normal = normalIndices[corner]! * 3;
      corners.set(positions.subarray(vertex, vertex + 3), corner * 3);
      cornerNormals.set(normals.subarray(normal, normal + 3), corner * 3);
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(corners, 3));
    geometry.setAttribute("normal", new THREE.BufferAttribute(cornerNormals, 3));
    return geometry;
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("normal", new THREE.BufferAttribute(normals, 3));
  geometry.setIndex(new THREE.BufferAttribute(indices, 1));
  return geometry;
}

function primitiveGeometry(module: MainModule, model: MjModel, geomId: number): THREE.BufferGeometry {
  const type = model.geom_type[geomId] as number;
  const offset = geomId * 3;
  const x = Math.max(0.001, model.geom_size[offset] as number);
  const y = Math.max(0.001, model.geom_size[offset + 1] as number);
  const z = Math.max(0.001, model.geom_size[offset + 2] as number);
  if (type === module.mjtGeom.mjGEOM_BOX.value) {
    return new THREE.BoxGeometry(x * 2, y * 2, z * 2);
  }
  if (type === module.mjtGeom.mjGEOM_SPHERE.value) {
    return new THREE.SphereGeometry(x, 20, 14);
  }
  if (type === module.mjtGeom.mjGEOM_CYLINDER.value) {
    const geometry = new THREE.CylinderGeometry(x, x, y * 2, 24);
    geometry.rotateX(Math.PI / 2);
    return geometry;
  }
  throw new Error(`不支持的可视几何类型：${type}`);
}

export function resolvedGeomRgba(model: MjModel, geomId: number): [number, number, number, number] {
  const geomOffset = geomId * 4;
  const geomRgba: [number, number, number, number] = [
    model.geom_rgba[geomOffset] as number,
    model.geom_rgba[geomOffset + 1] as number,
    model.geom_rgba[geomOffset + 2] as number,
    model.geom_rgba[geomOffset + 3] as number,
  ];
  const hasExplicitGeomRgba = geomRgba.some(
    (value, index) => Math.abs(value - (DEFAULT_GEOM_RGBA[index] ?? value)) > 1e-6,
  );
  const materialId = model.geom_matid[geomId] as number;
  if (materialId < 0 || hasExplicitGeomRgba) return geomRgba;
  const materialOffset = materialId * 4;
  return [
    model.mat_rgba[materialOffset] as number,
    model.mat_rgba[materialOffset + 1] as number,
    model.mat_rgba[materialOffset + 2] as number,
    model.mat_rgba[materialOffset + 3] as number,
  ];
}

function materialForGeom(
  model: MjModel,
  geomId: number,
  cache: Map<string, THREE.MeshStandardMaterial>,
  flatShading: boolean,
): THREE.MeshStandardMaterial {
  const [red, green, blue, alpha] = resolvedGeomRgba(model, geomId);
  const key = [red, green, blue, alpha].map((value) => value.toFixed(4)).join(":") + ":" + flatShading;
  let material = cache.get(key);
  if (!material) {
    material = new THREE.MeshStandardMaterial({
      color: new THREE.Color(red, green, blue),
      roughness: 0.8,
      metalness: NEUTRAL_METALNESS,
      flatShading,
      transparent: alpha < 0.999,
      opacity: alpha,
      depthWrite: alpha >= 0.999,
    });
    cache.set(key, material);
  }
  return material;
}

function toMujoco(vector: THREE.Vector3): THREE.Vector3 {
  return new THREE.Vector3(vector.x, -vector.z, vector.y);
}

// The only world-frame conversion: MuJoCo is Z-up; three.js is Y-up.
function toThree(vector: THREE.Vector3): THREE.Vector3 {
  return new THREE.Vector3(vector.x, vector.z, -vector.y);
}

function setGeomMatrix(
  object: THREE.Mesh,
  positions: ArrayLike<number>,
  matrices: ArrayLike<number>,
  geomId: number,
): void {
  const p = geomId * 3;
  const r = geomId * 9;
  // Compiled vertices remain in MuJoCo local coordinates, so this is C * R,
  // not C * R * C^-1. Translation is C * p.
  object.matrix.set(
    matrices[r], matrices[r + 1], matrices[r + 2], positions[p],
    matrices[r + 6], matrices[r + 7], matrices[r + 8], positions[p + 2],
    -(matrices[r + 3] as number), -(matrices[r + 4] as number), -(matrices[r + 5] as number), -(positions[p + 1] as number),
    0, 0, 0, 1,
  );
  object.matrixWorldNeedsUpdate = true;
}

export class ViewerCore {
  static async create(options: ViewerCoreOptions): Promise<ViewerCore> {
    const module = await getMujoco();
    const assetPackage = await loadAssetPackage(options.assetUrl);
    const vfs = new module.MjVFS();
    let model: MjModel | null = null;
    let data: MjData | null = null;
    let appearance: Appearance | null = null;
    try {
      for (const [path, bytes] of assetPackage.files) vfs.addBuffer(path, bytes);
      model = module.MjModel.from_xml_string(assetPackage.xml, vfs);
      if (Math.abs(model.opt.timestep - TIMESTEP) > 1e-9) {
        throw new Error("场景时间步与实验协议不一致");
      }
      data = new module.MjData(model);
      module.mj_forward(model, data);
      const xml = new DOMParser().parseFromString(assetPackage.xml, "application/xml");
      const meshElements = new Map([...xml.querySelectorAll("asset > mesh")].map((element) => [element.getAttribute("name"), element]));
      const flatMeshes = new Set<number>();
      const checkedMeshes = new Set<number>();
      for (let geom = 0; geom < model.ngeom; geom += 1) {
        if (!c1ShouldRender(module, model, geom) || model.geom_type[geom] !== module.mjtGeom.mjGEOM_MESH.value) continue;
        const mesh = model.geom_dataid[geom] as number;
        if (checkedMeshes.has(mesh)) continue;
        checkedMeshes.add(mesh);
        const name = module.mj_id2name(model, module.mjtObj.mjOBJ_MESH.value, mesh);
        const element = name ? meshElements.get(name) : undefined;
        if (element && meshUsesFaceNormals(element, assetPackage.files)) flatMeshes.add(mesh);
      }
      appearance = await loadAppearance(options.appearanceUrl ?? null);
      return new ViewerCore(module, model, data, vfs, options, flatMeshes, appearance);
    } catch (error) {
      data?.delete();
      model?.delete();
      vfs.delete();
      if (appearance) disposeAppearance(appearance);
      throw error;
    }
  }

  readonly model: MjModel;
  readonly data: MjData;

  private readonly module: MainModule;
  private readonly vfs: MjVFS;
  private readonly canvas: HTMLCanvasElement;
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(38, 1, 0.01, 100);
  private readonly controls: OrbitControls;
  private readonly renderGeoms: RenderGeom[] = [];
  private readonly geometries = new Set<THREE.BufferGeometry>();
  private readonly materials = new Map<string, THREE.MeshStandardMaterial>();
  private readonly raycaster = new THREE.Raycaster();
  private readonly pointer = new THREE.Vector2();
  private readonly forceArrow = new THREE.ArrowHelper(new THREE.Vector3(1, 0, 0), new THREE.Vector3(), 0.1, 0xc53d35);
  private readonly coverageJoints: CoverageJoint[] = [];
  private readonly onTelemetry: ((telemetry: ViewerTelemetry) => void) | undefined;
  private readonly frameTimes: number[] = [];
  private readonly frameSteps: number[] = [];
  private simulatedSteps = 0;
  private readonly dragArrowOrigin = new THREE.Vector3();
  private readonly dragArrowForce = new THREE.Vector3();
  private drag: DragState | null = null;
  private frameRadius = 1;
  private activeMs = 0;
  private dragCount = 0;
  private zoomHintTimer = 0;
  private resetCount = 0;
  private disposed = false;
  private lastTelemetryAt = 0;
  private simulationFaultCount = 0;
  private faulted = false;
  private paused = false;
  private readonly originalMaterials = new Map<THREE.Mesh, THREE.Material>();
  private readonly partMaterials = new Map<string, THREE.MeshStandardMaterial>();
  private readonly flatGeoms = new Set<number>();
  private readonly movableParts: MovablePart[] = [];
  private readonly geomMovers = new Map<number, number>();
  private readonly highlightMaterials = new Map<boolean, THREE.MeshStandardMaterial>();
  private highlightedPart: number | null = null;
  private partColorsEnabled = false;
  private ground: GroundVisual | null = null;
  private readonly extraGrounds: GroundVisual[] = [];
  /** Body whose translation the contact shadow follows (the scene's freejoint root), or -1. */
  private rootBody = -1;
  private readonly rootRestPosition = new THREE.Vector3();
  private rootRestYaw = 0;
  private readonly initialCameraDirection: THREE.Vector3;

  private constructor(
    module: MainModule,
    model: MjModel,
    data: MjData,
    vfs: MjVFS,
    options: ViewerCoreOptions,
    private readonly flatMeshes: ReadonlySet<number>,
    private readonly appearance: Appearance,
  ) {
    this.module = module;
    this.model = model;
    this.data = data;
    this.vfs = vfs;
    this.canvas = options.canvas;
    this.onTelemetry = options.onTelemetry;
    this.initialCameraDirection = options.initialCameraDirection
      ? new THREE.Vector3(...options.initialCameraDirection).normalize()
      : new THREE.Vector3(1, 0.72, 1.08).normalize();
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: false,
      alpha: false,
      powerPreference: "high-performance",
    });
    this.renderer.setPixelRatio(1);
    this.renderer.setClearColor(GROUND_CLEAR_COLOR, 1);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.controls = new OrbitControls(this.camera, this.canvas);
    this.controls.enableDamping = true;
    this.controls.enablePan = false;
    this.controls.zoomSpeed = 0.8;
    this.canvas.style.touchAction = "none";
    this.buildScene();
    this.buildMovableParts();
    this.controls.saveState();
    this.buildCoverageJoints();
    this.installInteraction();
    this.forceArrow.visible = false;
    this.scene.add(this.forceArrow);
    this.canvas.dataset.ready = "true";
    updateDebugStats(1, 0);
  }

  private buildScene(): void {
    this.scene.add(new THREE.HemisphereLight(0xffffff, 0x667068, 2.2));
    const key = new THREE.DirectionalLight(0xffffff, 2.5);
    key.position.set(-4, 7, 5);
    this.scene.add(key);
    const fill = new THREE.DirectionalLight(0xbfd7e8, 1.0);
    fill.position.set(5, 3, -4);
    this.scene.add(fill);

    const meshCache = new Map<number, THREE.BufferGeometry>();
    const planeGeoms: number[] = [];
    for (let geomId = 0; geomId < this.model.ngeom; geomId += 1) {
      if (!c1ShouldRender(this.module, this.model, geomId)) continue;
      const type = this.model.geom_type[geomId] as number;
      if (type === this.module.mjtGeom.mjGEOM_PLANE.value) { planeGeoms.push(geomId); continue; }
      const meshId = this.model.geom_dataid[geomId] as number;
      const meshName = type === this.module.mjtGeom.mjGEOM_MESH.value
        ? this.module.mj_id2name(this.model, this.module.mjtObj.mjOBJ_MESH.value, meshId) : null;
      const source = meshName ? this.appearance.meshes.get(meshName) : undefined;
      const bodyId = this.model.geom_bodyid[geomId] as number;
      if (source) {
        // MuJoCo centers/rotates compiled meshes. Original GLB vertices require
        // undoing that compiler frame before applying source scale/node transforms.
        const pos = meshId * 3, quat = meshId * 4;
        const inverseCompiler = new THREE.Matrix4().compose(
          new THREE.Vector3(this.model.mesh_pos[pos], this.model.mesh_pos[pos + 1], this.model.mesh_pos[pos + 2]),
          new THREE.Quaternion(this.model.mesh_quat[quat + 1], this.model.mesh_quat[quat + 2], this.model.mesh_quat[quat + 3], this.model.mesh_quat[quat]),
          new THREE.Vector3(1, 1, 1),
        ).invert();
        const scale = new THREE.Matrix4().makeScale(source.scale[0]!, source.scale[1]!, source.scale[2]!);
        for (const mesh of source.meshes) {
          const object = new THREE.Mesh(mesh.geometry, mesh.material);
          object.matrixAutoUpdate = false;
          object.frustumCulled = false;
          object.userData.geomId = geomId; object.userData.bodyId = bodyId;
          this.scene.add(object);
          const localMatrix = inverseCompiler.clone().multiply(scale).multiply(mesh.matrixWorld);
          this.renderGeoms.push({ geomId, bodyId, object, localMatrix });
          this.originalMaterials.set(object, object.material);
        }
        continue;
      }
      let geometry: THREE.BufferGeometry;
      if (type === this.module.mjtGeom.mjGEOM_MESH.value) {
        const meshId = this.model.geom_dataid[geomId] as number;
        const existing = meshCache.get(meshId);
        geometry = existing ?? meshGeometry(this.model, meshId);
        if (!existing) meshCache.set(meshId, geometry);
      } else {
        geometry = primitiveGeometry(this.module, this.model, geomId);
      }
      this.geometries.add(geometry);
      const flat = type === this.module.mjtGeom.mjGEOM_MESH.value && this.flatMeshes.has(this.model.geom_dataid[geomId] as number);
      if (flat) this.flatGeoms.add(geomId);
      const name = this.module.mj_id2name(this.model, this.module.mjtObj.mjOBJ_GEOM.value, geomId) || "";
      let material = materialForGeom(this.model, geomId, this.materials, flat);
      if (this.appearance.neutralGeoms.has(name)) {
        const key = "neutral:" + flat;
        material = this.materials.get(key) ?? new THREE.MeshStandardMaterial({ color: 0xb8b8b8, roughness: 0.8, flatShading: flat });
        this.materials.set(key, material);
      }
      const object = new THREE.Mesh(geometry, material);
      object.matrixAutoUpdate = false;
      object.frustumCulled = false;
      object.userData.geomId = geomId;
      object.userData.bodyId = bodyId;
      this.scene.add(object);
      this.renderGeoms.push({ geomId, bodyId, object });
      this.originalMaterials.set(object, object.material);
    }

    this.updateGeomMatrices();
    const frame = this.renderFrame();
    this.frameRadius = frame.radius;
    this.buildGround(planeGeoms, frame.radius);
    const distance = (frame.radius / Math.tan(THREE.MathUtils.degToRad(this.camera.fov / 2))) * 1.55;
    this.camera.near = Math.max(0.005, distance / 150);
    this.camera.far = Math.max(30, distance * 25);
    this.camera.position.copy(frame.center).addScaledVector(this.initialCameraDirection, distance);
    this.controls.target.copy(frame.center);
    this.controls.minDistance = Math.max(0.05, distance * 0.18);
    this.controls.maxDistance = distance * 5;
    this.camera.lookAt(frame.center);
    this.camera.updateProjectionMatrix();
    this.controls.update();
  }

  /**
   * Render plane geoms as a metre grid with distance fade; the first horizontal one also carries a
   * soft contact shadow sized from the object's resting footprint. Presentation only: the plane
   * geom, its collision flags and the physics are untouched (see ground.ts).
   */
  private buildGround(planeGeoms: number[], objectRadius: number): void {
    if (planeGeoms.length === 0) return;
    this.updateGeomMatrices();
    const footprint = this.objectFootprint();
    const positions = this.data.geom_xpos;
    const matrices = this.data.geom_xmat;
    this.rootBody = footprint ? this.findRootBody() : -1;
    if (this.rootBody >= 0) {
      const at = this.rootBody * 3;
      this.rootRestPosition.set(this.data.xpos[at] as number, this.data.xpos[at + 1] as number, this.data.xpos[at + 2] as number);
      this.rootRestYaw = this.rootYaw();
    }
    let shadowed = false;
    for (const geomId of planeGeoms) {
      const horizontal = (matrices[geomId * 9 + 8] as number) > 0.9;
      const bodyId = this.model.geom_bodyid[geomId] as number;
      const { plane, visual } = buildGround({
        renderer: this.renderer,
        planeHalfSize: [this.model.geom_size[geomId * 3] as number, this.model.geom_size[geomId * 3 + 1] as number],
        objectRadius,
        footprint: horizontal && !shadowed ? footprint : null,
        groundY: positions[geomId * 3 + 2] as number,
        anchor: this.rootBody >= 0 ? toThree(this.rootRestPosition) : null,
      });
      if (horizontal && !shadowed && footprint) {
        shadowed = true;
        this.ground = visual;
      }
      plane.userData.geomId = geomId;
      plane.userData.bodyId = bodyId;
      for (const object of visual.objects) this.scene.add(object);
      this.renderGeoms.push({ geomId, bodyId, object: plane });
      this.originalMaterials.set(plane, plane.material);
      if (this.ground !== visual) this.extraGrounds.push(visual);
    }
  }

  /** Heading of the root body about MuJoCo +Z (equal to three.js +Y after the frame conversion). */
  private rootYaw(): number {
    const at = this.rootBody * 9;
    return Math.atan2(this.data.xmat[at + 3] as number, this.data.xmat[at] as number);
  }

  private findRootBody(): number {
    for (let joint = 0; joint < this.model.njnt; joint += 1) {
      if (this.model.jnt_type[joint] === this.module.mjtJoint.mjJNT_FREE.value) return this.model.jnt_bodyid[joint] as number;
    }
    for (let body = 1; body < this.model.nbody; body += 1) {
      if ((this.model.body_dofnum[body] as number) > 0) return body;
    }
    return -1;
  }

  /** World bounds (three.js frame) of everything rendered except scene planes, at the current pose. */
  private renderBounds(): GroundFootprint | null {
    const min = new THREE.Vector3(Infinity, Infinity, Infinity);
    const max = new THREE.Vector3(-Infinity, -Infinity, -Infinity);
    const corner = new THREE.Vector3();
    for (const { object } of this.renderGeoms) {
      const geometry = object.geometry;
      if (!geometry.boundingBox) geometry.computeBoundingBox();
      const box = geometry.boundingBox;
      if (!box || box.isEmpty()) continue;
      for (let bits = 0; bits < 8; bits += 1) {
        corner.set(bits & 1 ? box.max.x : box.min.x, bits & 2 ? box.max.y : box.min.y, bits & 4 ? box.max.z : box.min.z);
        corner.applyMatrix4(object.matrix);
        min.min(corner);
        max.max(corner);
      }
    }
    return Number.isFinite(min.x) ? { min, max } : null;
  }

  private renderFrame(): { center: THREE.Vector3; radius: number } {
    const bounds = this.renderBounds();
    if (!bounds) return { center: new THREE.Vector3(), radius: 1 };
    const center = bounds.min.clone().add(bounds.max).multiplyScalar(0.5);
    const size = bounds.max.clone().sub(bounds.min);
    return { center, radius: Math.max(0.25, size.x / 2, size.y / 2, size.z / 2) };
  }

  private objectFootprint(): GroundFootprint | null {
    return this.renderBounds();
  }

  private updateGeomMatrices(): void {
    const positions = this.data.geom_xpos;
    const matrices = this.data.geom_xmat;
    for (const { geomId, object, localMatrix } of this.renderGeoms) {
      setGeomMatrix(object, positions, matrices, geomId);
      if (localMatrix) object.matrix.multiply(localMatrix);
    }
  }

  private buildCoverageJoints(): void {
    for (let jointId = 0; jointId < this.model.njnt; jointId += 1) {
      const type = this.model.jnt_type[jointId] as number;
      if (type !== this.module.mjtJoint.mjJNT_HINGE.value && type !== this.module.mjtJoint.mjJNT_SLIDE.value) continue;
      const rangeOffset = jointId * 2;
      const lower = this.model.jnt_range[rangeOffset] as number;
      const upper = this.model.jnt_range[rangeOffset + 1] as number;
      if (!(upper > lower)) continue;
      const qposAddress = this.model.jnt_qposadr[jointId] as number;
      const initial = this.data.qpos[qposAddress] as number;
      this.coverageJoints.push({ qposAddress, lower, upper, visitedMin: initial, visitedMax: initial });
    }
  }

  private buildMovableParts(): void {
    const movers = new Map<number, Set<MovablePart["motions"][number]>>();
    for (let joint = 0; joint < this.model.njnt; joint += 1) {
      const type = this.model.jnt_type[joint] as number;
      const motion = type === this.module.mjtJoint.mjJNT_HINGE.value ? "hinge"
        : type === this.module.mjtJoint.mjJNT_SLIDE.value ? "slide"
        : type === this.module.mjtJoint.mjJNT_BALL.value ? "ball" : null;
      // The scene's root freejoint enables whole-object dragging, not articulation.
      if (!motion) continue;
      // A declared joint is a place to try, not a guarantee of reachable motion.
      const body = this.model.jnt_bodyid[joint] as number;
      const motions = movers.get(body) ?? new Set<MovablePart["motions"][number]>();
      motions.add(motion);
      movers.set(body, motions);
    }
    for (const { geomId, bodyId } of this.renderGeoms) {
      let owner = bodyId;
      while (owner > 0 && !movers.has(owner)) owner = this.model.body_parentid[owner] as number;
      if (owner > 0) this.geomMovers.set(geomId, owner);
    }
    const visibleOwners = new Set(this.geomMovers.values());
    for (const [id, motions] of movers) {
      if (visibleOwners.has(id)) this.movableParts.push({ id, number: this.movableParts.length + 1, motions: [...motions] });
    }
  }

  getMovableParts(): MovablePart[] {
    return this.movableParts.map((part) => ({ ...part, motions: [...part.motions] }));
  }

  highlightMovablePart(id: number | null): void {
    this.highlightedPart = this.movableParts.some((part) => part.id === id) ? id : null;
    this.applyDisplayMaterials();
  }

  private installInteraction(): void {
    this.canvas.addEventListener("wheel", this.handleWheel, { capture: true, passive: true });
    this.canvas.addEventListener("pointerdown", this.handlePointerDown, true);
    this.canvas.addEventListener("pointermove", this.handlePointerMove, true);
    this.canvas.addEventListener("pointerup", this.handlePointerUp, true);
    this.canvas.addEventListener("pointercancel", this.handlePointerUp, true);
  }

  private pointerNdc(event: PointerEvent): void {
    const rect = this.canvas.getBoundingClientRect();
    this.pointer.set(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );
  }

  private isDynamicBody(bodyId: number): boolean {
    let current = bodyId;
    while (current > 0) {
      if ((this.model.body_dofnum[current] as number) > 0) return true;
      current = this.model.body_parentid[current] as number;
    }
    return false;
  }

  /**
   * Zoom only on a deliberate gesture: Ctrl/⌘ + wheel, or a trackpad pinch (which browsers
   * deliver as a wheel event with ctrlKey set). A plain wheel is left to the page, because the
   * canvas sits inside a scrolling pane and grabbing every wheel over it turned a participant's
   * page scroll into a camera zoom whenever the cursor crossed the viewer.
   *
   * OrbitControls returns before calling preventDefault when zoom is disabled, so a plain wheel
   * reaches the page untouched; with a modifier held it zooms and suppresses the browser's own
   * page zoom. Zoom is re-enabled after this dispatch so touch pinch is never affected.
   */
  private handleWheel = (event: WheelEvent): void => {
    const deliberate = event.ctrlKey || event.metaKey;
    this.controls.enableZoom = deliberate;
    window.setTimeout(() => { if (!this.disposed) this.controls.enableZoom = true; }, 0);
    if (deliberate) return;
    this.canvas.dataset.zoomHint = "on";
    window.clearTimeout(this.zoomHintTimer);
    this.zoomHintTimer = window.setTimeout(() => { delete this.canvas.dataset.zoomHint; }, 1400);
  };

  private handlePointerDown = (event: PointerEvent): void => {
    if (event.button !== 0 || this.drag) return;
    this.pointerNdc(event);
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const hits = this.raycaster.intersectObjects(this.renderGeoms.map((entry) => entry.object), false);
    const onModel = hits.filter((entry) => {
      const bodyId = entry.object.userData.bodyId as number;
      const geomId = entry.object.userData.geomId as number;
      const name = this.module.mj_id2name(this.model, this.module.mjtObj.mjOBJ_GEOM.value, geomId) || "";
      return bodyId > 0 && !name.startsWith("us_scene_");
    });
    const hit = onModel.find((entry) => this.isDynamicBody(entry.object.userData.bodyId as number));
    if (!hit) {
      // Pressing a welded part is a real attempt that simply cannot move. Counting it is what
      // stops a participant who tried the one fixed part from being told they never tried.
      // Pressing empty space or the ground is camera work and is deliberately not counted.
      if (onModel.length) {
        this.dragCount += 1;
        this.emitTelemetry(true);
      }
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    const bodyId = hit.object.userData.bodyId as number;
    const hitMj = toMujoco(hit.point);
    const positionOffset = bodyId * 3;
    const bodyPosition = new THREE.Vector3(
      this.data.xpos[positionOffset] as number,
      this.data.xpos[positionOffset + 1] as number,
      this.data.xpos[positionOffset + 2] as number,
    );
    const delta = hitMj.clone().sub(bodyPosition);
    const matrixOffset = bodyId * 9;
    const rotation = this.data.xmat;
    const localAnchor = new THREE.Vector3(
      (rotation[matrixOffset] as number) * delta.x + (rotation[matrixOffset + 3] as number) * delta.y + (rotation[matrixOffset + 6] as number) * delta.z,
      (rotation[matrixOffset + 1] as number) * delta.x + (rotation[matrixOffset + 4] as number) * delta.y + (rotation[matrixOffset + 7] as number) * delta.z,
      (rotation[matrixOffset + 2] as number) * delta.x + (rotation[matrixOffset + 5] as number) * delta.y + (rotation[matrixOffset + 8] as number) * delta.z,
    );
    const normal = this.camera.getWorldDirection(new THREE.Vector3());
    this.drag = {
      bodyId,
      geomId: hit.object.userData.geomId as number,
      localAnchor,
      previousAnchor: hitMj.clone(),
      target: hitMj,
      plane: new THREE.Plane().setFromNormalAndCoplanarPoint(normal, hit.point),
      pointerId: event.pointerId,
    };
    this.paused = false;
    this.dragCount += 1;
    this.controls.enabled = false;
    this.canvas.setPointerCapture(event.pointerId);
    this.canvas.dataset.dragging = "true";
    this.emitTelemetry(true);
  };

  private handlePointerMove = (event: PointerEvent): void => {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.pointerNdc(event);
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const target = this.raycaster.ray.intersectPlane(this.drag.plane, new THREE.Vector3());
    if (target) this.drag.target.copy(toMujoco(target));
  };

  private handlePointerUp = (event: PointerEvent): void => {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.drag = null;
    this.controls.enabled = true;
    this.forceArrow.visible = false;
    this.data.xfrc_applied.fill(0);
    delete this.canvas.dataset.dragging;
    if (this.canvas.hasPointerCapture(event.pointerId)) this.canvas.releasePointerCapture(event.pointerId);
  };

  private applyDragForce(): void {
    this.data.xfrc_applied.fill(0);
    if (!this.drag) return;
    const bodyId = this.drag.bodyId;
    const positionOffset = bodyId * 3;
    const matrixOffset = bodyId * 9;
    const rotation = this.data.xmat;
    const local = this.drag.localAnchor;
    const rotatedAnchor = new THREE.Vector3(
      (rotation[matrixOffset] as number) * local.x + (rotation[matrixOffset + 1] as number) * local.y + (rotation[matrixOffset + 2] as number) * local.z,
      (rotation[matrixOffset + 3] as number) * local.x + (rotation[matrixOffset + 4] as number) * local.y + (rotation[matrixOffset + 5] as number) * local.z,
      (rotation[matrixOffset + 6] as number) * local.x + (rotation[matrixOffset + 7] as number) * local.y + (rotation[matrixOffset + 8] as number) * local.z,
    );
    const bodyPosition = new THREE.Vector3(
      this.data.xpos[positionOffset] as number,
      this.data.xpos[positionOffset + 1] as number,
      this.data.xpos[positionOffset + 2] as number,
    );
    const anchor = bodyPosition.clone().add(rotatedAnchor);
    const mass = Math.max(0.01, this.model.body_subtreemass[bodyId] as number);
    const stiffness = mass * DRAG_NATURAL_FREQUENCY ** 2;
    const damping = 2 * Math.sqrt(stiffness * mass);
    const velocity = anchor.clone().sub(this.drag.previousAnchor).multiplyScalar(1 / TIMESTEP);
    this.drag.previousAnchor.copy(anchor);
    const force = this.drag.target.clone().sub(anchor).multiplyScalar(stiffness).addScaledVector(velocity, -damping);
    const cap = mass * DRAG_MAX_ACCELERATION;
    if (force.length() > cap) force.setLength(cap);
    const inertialPosition = new THREE.Vector3(
      this.data.xipos[positionOffset] as number,
      this.data.xipos[positionOffset + 1] as number,
      this.data.xipos[positionOffset + 2] as number,
    );
    const torque = anchor.clone().sub(inertialPosition).cross(force);
    const forceOffset = bodyId * 6;
    this.data.xfrc_applied[forceOffset] = force.x;
    this.data.xfrc_applied[forceOffset + 1] = force.y;
    this.data.xfrc_applied[forceOffset + 2] = force.z;
    this.data.xfrc_applied[forceOffset + 3] = torque.x;
    this.data.xfrc_applied[forceOffset + 4] = torque.y;
    this.data.xfrc_applied[forceOffset + 5] = torque.z;

    this.dragArrowOrigin.copy(anchor);
    this.dragArrowForce.copy(force).multiplyScalar(1 / mass);
  }

  step(): void {
    if (this.disposed || this.faulted || this.paused) return;
    this.applyDragForce();
    const before = this.data.time;
    this.module.mj_step(this.model, this.data);
    this.simulatedSteps += 1;
    if (this.data.time < before || !Array.from(this.data.qpos).every(Number.isFinite)) {
      this.simulationFaultCount += 1;
      this.faulted = true;
      this.module.mj_resetData(this.model, this.data);
      this.module.mj_forward(this.model, this.data);
      this.emitTelemetry(true);
      return;
    }
    for (const joint of this.coverageJoints) {
      const value = this.data.qpos[joint.qposAddress] as number;
      joint.visitedMin = Math.min(joint.visitedMin, value);
      joint.visitedMax = Math.max(joint.visitedMax, value);
    }
  }

  advanceActive(deltaMs: number): void {
    if (!this.disposed && document.visibilityState === "visible") this.activeMs += Math.max(0, deltaMs);
  }

  render(now: number): void {
    if (this.disposed) return;
    const width = Math.max(1, this.canvas.clientWidth);
    const height = Math.max(1, this.canvas.clientHeight);
    if (this.canvas.width !== width || this.canvas.height !== height) this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.updateGeomMatrices();
    if (this.ground && this.rootBody >= 0) {
      const at = this.rootBody * 3;
      const dx = (this.data.xpos[at] as number) - this.rootRestPosition.x;
      const dy = (this.data.xpos[at + 1] as number) - this.rootRestPosition.y;
      const lift = (this.data.xpos[at + 2] as number) - this.rootRestPosition.z;
      // MuJoCo (x, y) on the ground maps to three.js (x, -z).
      this.ground.follow(dx, -dy, this.rootYaw() - this.rootRestYaw, lift);
    }
    const arrowForce = toThree(this.dragArrowForce);
    const arrowLength = arrowForce.length();
    if (this.drag && arrowLength > 1e-5) {
      this.forceArrow.position.copy(toThree(this.dragArrowOrigin));
      this.forceArrow.setDirection(arrowForce.normalize());
      this.forceArrow.setLength(
        Math.min(this.frameRadius, arrowLength * 0.002),
        this.frameRadius * 0.08,
        this.frameRadius * 0.04,
      );
      this.forceArrow.visible = true;
    } else {
      this.forceArrow.visible = false;
    }
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
    this.frameTimes.push(now);
    this.frameSteps.push(this.simulatedSteps);
    while ((this.frameTimes[0] ?? now) < now - 2_000) { this.frameTimes.shift(); this.frameSteps.shift(); }
    this.emitTelemetry(false, now);
  }

  reset(pause = false): void {
    if (this.disposed) return;
    this.drag = null;
    this.controls.enabled = true;
    this.forceArrow.visible = false;
    delete this.canvas.dataset.dragging;
    this.data.xfrc_applied.fill(0);
    this.module.mj_resetData(this.model, this.data);
    this.module.mj_forward(this.model, this.data);
    this.faulted = false;
    this.paused = pause;
    this.controls.reset();
    this.resetCount += 1;
    this.emitTelemetry(true);
  }

  getDragDebug(): ViewerDragDebug | null {
    if (!this.drag) return null;
    const bodyId = this.drag.bodyId;
    const positionOffset = bodyId * 3;
    const matrixOffset = bodyId * 9;
    const rotation = this.data.xmat;
    const local = this.drag.localAnchor;
    const anchor = new THREE.Vector3(
      this.data.xpos[positionOffset] as number,
      this.data.xpos[positionOffset + 1] as number,
      this.data.xpos[positionOffset + 2] as number,
    ).add(new THREE.Vector3(
      (rotation[matrixOffset] as number) * local.x + (rotation[matrixOffset + 1] as number) * local.y + (rotation[matrixOffset + 2] as number) * local.z,
      (rotation[matrixOffset + 3] as number) * local.x + (rotation[matrixOffset + 4] as number) * local.y + (rotation[matrixOffset + 5] as number) * local.z,
      (rotation[matrixOffset + 6] as number) * local.x + (rotation[matrixOffset + 7] as number) * local.y + (rotation[matrixOffset + 8] as number) * local.z,
    ));
    const cameraRight = toMujoco(this.camera.getWorldDirection(new THREE.Vector3()).cross(Y_AXIS).normalize());
    return {
      bodyId,
      geomId: this.drag.geomId,
      mass: this.model.body_subtreemass[bodyId] as number,
      anchor: [anchor.x, anchor.y, anchor.z],
      target: [this.drag.target.x, this.drag.target.y, this.drag.target.z],
      error: this.drag.target.clone().sub(anchor).length(),
      cameraRight: [cameraRight.x, cameraRight.y, cameraRight.z],
    };
  }

  restoreCounters(value: ViewerTelemetry): void {
    this.activeMs = value.activeMs;
    this.dragCount = value.dragCount;
    this.resetCount = value.resetCount;
    this.simulationFaultCount = value.simulationFaultCount ?? 0;
  }

  snapshot(): ViewerSnapshot {
    if (this.drag && this.canvas.hasPointerCapture(this.drag.pointerId)) {
      this.canvas.releasePointerCapture(this.drag.pointerId);
    }
    this.drag = null;
    this.controls.enabled = true;
    this.data.xfrc_applied.fill(0);
    this.forceArrow.visible = false;
    delete this.canvas.dataset.dragging;
    const spec = this.module.mjtState.mjSTATE_INTEGRATION.value;
    const buffer = new this.module.DoubleBuffer(this.module.mj_stateSize(this.model, spec));
    let physics: number[];
    try {
      // Output parameters need a WasmBuffer; an external JS array is not copied back.
      this.module.mj_getState(this.model, this.data, buffer, spec);
      physics = Array.from(buffer.GetView() as Float64Array);
    } finally {
      buffer.delete();
    }
    return { physics, paused: this.paused, camera: this.camera.position.toArray(),
      target: this.controls.target.toArray(), telemetry: this.getTelemetry(),
      coverage: this.coverageJoints.map((joint) => [joint.visitedMin, joint.visitedMax]) };
  }

  restore(snapshot: ViewerSnapshot): void {
    this.module.mj_setState(this.model, this.data, snapshot.physics, this.module.mjtState.mjSTATE_INTEGRATION.value);
    this.module.mj_forward(this.model, this.data);
    this.camera.position.fromArray(snapshot.camera);
    this.controls.target.fromArray(snapshot.target);
    this.restoreCounters(snapshot.telemetry);
    this.faulted = snapshot.telemetry.faulted ?? false;
    this.paused = snapshot.paused;
    snapshot.coverage.forEach(([low, high], index) => {
      const joint = this.coverageJoints[index];
      if (joint) { joint.visitedMin = low; joint.visitedMax = high; }
    });
    this.controls.update();
  }

  setPartColors(enabled: boolean): void {
    this.partColorsEnabled = enabled;
    this.applyDisplayMaterials();
  }

  private applyDisplayMaterials(): void {
    for (const { geomId, bodyId, object } of this.renderGeoms) {
      if (bodyId === 0) continue;
      const flatShading = this.flatGeoms.has(geomId) || this.originalMaterials.get(object)?.flatShading === true;
      if (this.highlightedPart !== null && this.geomMovers.get(geomId) === this.highlightedPart) {
        let material = this.highlightMaterials.get(flatShading);
        if (!material) {
          material = new THREE.MeshStandardMaterial({ color: 0xe8a23b, roughness: 0.7, flatShading });
          this.highlightMaterials.set(flatShading, material);
        }
        object.material = material;
        continue;
      }
      if (!this.partColorsEnabled) { object.material = this.originalMaterials.get(object)!; continue; }
      const key = bodyId + ":" + flatShading;
      let material = this.partMaterials.get(key);
      if (!material) {
        material = new THREE.MeshStandardMaterial({
          color: new THREE.Color().setHSL((bodyId * 0.618034) % 1, 0.62, 0.54), roughness: 0.7, flatShading,
        });
        this.partMaterials.set(key, material);
      }
      object.material = material;
    }
  }

  getTelemetry(): ViewerTelemetry {
    const coverage = this.coverageJoints.length === 0
      ? 0
      : this.coverageJoints.reduce((total, joint) => {
          const fraction = (joint.visitedMax - joint.visitedMin) / (joint.upper - joint.lower);
          return total + THREE.MathUtils.clamp(fraction, 0, 1);
        }, 0) / this.coverageJoints.length;
    const elapsed = (this.frameTimes.at(-1) ?? 0) - (this.frameTimes[0] ?? 0);
    const fps = elapsed > 0 ? ((this.frameTimes.length - 1) * 1000) / elapsed : 0;
    return {
      activeMs: Math.round(this.activeMs),
      dragCount: this.dragCount,
      resetCount: this.resetCount,
      jointCoverage: Number(coverage.toFixed(6)),
      fps: Number(fps.toFixed(3)),
      simulationFaultCount: this.simulationFaultCount,
      faulted: this.faulted,
      paused: this.paused,
      simulationRtf: elapsed > 0 ? ((this.frameSteps.at(-1) ?? 0) - (this.frameSteps[0] ?? 0)) * TIMESTEP * 1000 / elapsed : 0,
    };
  }

  private emitTelemetry(force: boolean, now = performance.now()): void {
    if (!this.onTelemetry || (!force && now - this.lastTelemetryAt < 200)) return;
    this.lastTelemetryAt = now;
    this.onTelemetry(this.getTelemetry());
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    delete this.canvas.dataset.ready;
    this.canvas.removeEventListener("wheel", this.handleWheel, { capture: true });
    this.canvas.removeEventListener("pointerdown", this.handlePointerDown, true);
    this.canvas.removeEventListener("pointermove", this.handlePointerMove, true);
    this.canvas.removeEventListener("pointerup", this.handlePointerUp, true);
    this.canvas.removeEventListener("pointercancel", this.handlePointerUp, true);
    window.clearTimeout(this.zoomHintTimer);
    delete this.canvas.dataset.zoomHint;
    this.controls.dispose();
    for (const geometry of this.geometries) geometry.dispose();
    for (const material of this.materials.values()) material.dispose();
    for (const material of this.partMaterials.values()) material.dispose();
    for (const material of this.highlightMaterials.values()) material.dispose();
    disposeAppearance(this.appearance);
    this.ground?.dispose();
    for (const ground of this.extraGrounds) ground.dispose();
    this.forceArrow.line.geometry.dispose();
    (this.forceArrow.line.material as THREE.Material).dispose();
    this.forceArrow.cone.geometry.dispose();
    (this.forceArrow.cone.material as THREE.Material).dispose();
    this.renderer.renderLists.dispose();
    this.renderer.dispose();
    this.data.delete();
    this.model.delete();
    this.vfs.delete();
    updateDebugStats(0, 1);
  }
}

export class LockstepDriver {
  private animationFrame = 0;
  private previousTimestamp: number | null = null;
  private accumulator = 0;
  private running = false;

  constructor(private readonly viewers: readonly ViewerCore[]) {}

  start(): void {
    if (this.running) return;
    this.previousTimestamp = null;
    this.accumulator = 0;
    this.running = true;
    this.animationFrame = requestAnimationFrame(this.frame);
  }

  private frame = (timestamp: number): void => {
    if (!this.running) return;
    if (document.visibilityState !== "visible") {
      this.previousTimestamp = null;
      this.accumulator = 0;
      this.animationFrame = requestAnimationFrame(this.frame);
      return;
    }
    const activeMs = this.previousTimestamp === null ? 0 : Math.min(250, timestamp - this.previousTimestamp);
    const deltaSeconds = this.previousTimestamp === null
      ? STEPS_PER_FRAME * TIMESTEP
      : Math.min((timestamp - this.previousTimestamp) / 1000, STEPS_PER_FRAME * TIMESTEP);
    this.previousTimestamp = timestamp;
    this.accumulator += deltaSeconds;
    let steps = 0;
    const computeStarted = performance.now();
    while (this.accumulator + Number.EPSILON >= TIMESTEP && steps < STEPS_PER_FRAME) {
      for (const viewer of this.viewers) viewer.step();
      this.accumulator -= TIMESTEP;
      steps += 1;
      // Keep input/rendering responsive for expensive source assets, without changing dt.
      if (performance.now() - computeStarted >= 12) break;
    }
    this.accumulator = Math.min(this.accumulator, STEPS_PER_FRAME * TIMESTEP);
    for (const viewer of this.viewers) {
      viewer.advanceActive(activeMs);
      viewer.render(timestamp);
    }
    this.animationFrame = requestAnimationFrame(this.frame);
  };

  stop(): void {
    if (!this.running) return;
    this.running = false;
    cancelAnimationFrame(this.animationFrame);
  }
}
